import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import ListQuestionnaireView from '~/components/Shared/Questionnaire/List';
import { DEFAULT_PER_PAGE, NOTIFY_TYPE, SortType } from '~/constants/common';
import {
  QuestionnaireCategory,
  QuestionnaireWhereInput,
  SortOrder,
} from '~/models/common';

import { AppDispatch, RootState } from '~/store/store';
import { handleError, notify } from '~/utils/utils';
import {
  fetchListHealthCheck,
  postDeleteHealthCheckForm,
} from '~/store/slices/healthCheck';

const HealthCheckList = ({ isCompany }: any): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<QuestionnaireWhereInput>({
    category: {
      equals: QuestionnaireCategory.HealthCheckForm,
    },
  });
  const { t } = useTranslation();
  const currentPage = searchParams.get('page') || 1;
  const location = useLocation();
  const [sort, setSort] = React.useState<any>({
    createdAt: SortOrder.Desc,
  });
  const dispatch = useDispatch<AppDispatch>();

  const loading = useSelector((state: RootState) => state.healthCheck.loading);

  useEffect(() => {
    dispatch(
      fetchListHealthCheck({
        pagination: { page: Number(currentPage), perPage: DEFAULT_PER_PAGE },
        where: filters,
        orderBy: !sort.itemsName ? sort : undefined,
      }),
    ).then((res: any) => {
      if (res?.error) {
        handleError(res, t('common.error'));
      }
    });
  }, [currentPage, location.pathname, filters, sort]);

  const { data, pagination } = useSelector(
    (state: RootState) => state.healthCheck.listHealthCheck,
  );

  const handleChangePage = (page: number): void => {
    searchParams.set('page', page.toString());
    setSearchParams(searchParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const handleSort = ({ columnKey, order }): void => {
    if (sort[columnKey] === SortType[order]) {
      return;
    }
    setSort({
      [columnKey]: SortType[order] || undefined,
    });
    handleChangePage(1);
  };
  const handleDeleteQuestionnaire = async (id): Promise<void> => {
    Modal.confirm({
      title: (
        <span style={{ fontWeight: 300 }}>
          {t('healthCheck.list.modalTitle')}
        </span>
      ),
      okText: t('common.ok'),
      cancelText: t('common.cancel'),
      okButtonProps: {},
      cancelButtonProps: {},
      onOk: async (): Promise<void> => {
        const result: any = await dispatch(
          postDeleteHealthCheckForm({
            id,
            category: QuestionnaireCategory.HealthCheckForm,
          }),
        );
        if (!result?.error) {
          setFilters({ ...filters });
          notify(NOTIFY_TYPE.SUCCESS, t('healthCheck.delete.success')!);
        } else {
          handleError(result, t('healthCheck.delete.failure'), (errorInfo) => {
            return notify(NOTIFY_TYPE.ERROR, errorInfo?.message);
          });
        }
      },
    });
  };

  return (
    <>
      <ListQuestionnaireView
        isCompany={isCompany}
        handleChangePage={handleChangePage}
        setFilters={setFilters}
        handleSort={handleSort}
        dataSource={data}
        loading={loading}
        pagination={pagination}
        isQuestionnaireScreen={false}
        handleDeleteQuestionnaire={handleDeleteQuestionnaire}
        filters={filters}
      />
    </>
  );
};

export default HealthCheckList;
